/**
 * The Help pane, app-wide (System context PR B,
 * design/2026-10-14-help-agent-tours.md).
 *
 * The controller for the pane: it lives in App.tsx inside
 * PageToolsProvider, so the conversation and a tour survive moving
 * between pages. It reads the mounted page's tools and page context from the
 * page-tools registration slot (only the page on screen has tools), and
 * hands a tour to help/useTourRunner: navigate when the step is on another
 * page, wait for that page to mount, run the step's action through the page's
 * tools, outline the step's control, ask the help route to explain the step,
 * and wait for the person's Next.
 *
 * Moving to another section keeps the conversation and adds one "Now on
 * <section>." line (Adam, 14 Oct); Reset clears it.
 *
 * Until the person's first message (and outside a tour) the pane offers the
 * section's starter questions as pills, the same list its page helpers show:
 * the customer's from System context, else the shipped ones (Adam, 19 Oct).
 *
 * The Help button in the top bar (pages/Layout) opens it through useHelp().
 */
import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import useAccount from '../hooks/useAccount'
import useRouter from '../hooks/useRouter'
import { useAiAccess } from '../hooks/useAiAccess'
import { useHelpTours } from '../hooks/useHelpTours'
import { useHelpStarters } from '../hooks/useHelpStarters'
import { useHelpChat, type HelpTourContext } from '../hooks/useHelpChat'
import { NAV_PAGES, useOptionalPageToolsContext } from '../pageTools/PageToolsProvider'
import { renderChatMessage } from '../components/chat/utils'
import HelpPane from '../components/help/HelpPane'
import type { TourChoiceGroup } from '../components/help/HelpPane'
import { createHelpBridge } from './helpBridge'
import { helpReaderFor, sectionForRoute, sectionIntroFor, sectionsFor, starterQuestionsFor } from './features'
import { paneOpening, timeOfDayGreeting, useFirstName } from '../utils/greeting'
import type { Feature } from './features'
import { currentStep, tourFromHelpTour, tourFromSection } from './tourRuntime'
import type { TourRun, TourState } from './tourRuntime'
import { BUILT_IN_TOURS } from './tours'
import { useTourRunner } from './useTourRunner'
import routes from '../routes'
import { isRouteVisible } from '../pages/admin/AdminDrawer'
import type { RolePermissions } from '../types/app'

/** How long a composer setter has to land before its Send runs. */
const COMPOSER_SETTLE_MS = 150

interface HelpContextValue {
  /** Signed in, on a page that does not hide the assistant. */
  available: boolean
  open: boolean
  handleOpen: () => void
  handleClose: () => void
  /** Open the pane and start a tour, from the pane's menu or any other caller (a voice provider later). */
  startTour: (run: TourRun) => void
  /** The tour in progress, if any. */
  tour: TourState
  /** End the pane's tour, if one runs: a walkthrough does before it starts, since both draw the one outline ring. */
  endTour: () => void
}

const HelpContext = createContext<HelpContextValue | null>(null)

/** The Help pane's open state, or null outside the provider. */
export function useHelp(): HelpContextValue | null {
  return useContext(HelpContext)
}

/** The tour step on screen, as the help route takes it. */
function tourContext(state: TourState): HelpTourContext | undefined {
  const step = currentStep(state)
  if (!state.tour || !step) return undefined
  return {
    name: state.tour.name,
    step: state.index + 1,
    total: state.tour.steps.length,
    feature: step.feature,
    control: step.control,
    say: step.say,
    type: step.type,
    did: state.did,
  }
}

/**
 * The pages the Help pane may take this person to: never a page the pane is
 * hidden on (hideAssistant), and an admin page only when the admin menu would
 * show it to them (isRouteVisible). Read when the tool list is built, so a
 * role change applies at once. Routes are read at call time, not import time.
 */
export function navPagesFor(perms: RolePermissions | undefined): string[] {
  const isManager = !!perms?.isManager
  const isRealmAdmin = !!perms?.isRealmAdmin
  const meta = new Map(routes.map((r) => [r.name, r.meta]))
  return Object.keys(NAV_PAGES).filter((k) => {
    if (meta.get(k)?.hideAssistant) return false
    if (k === 'admin') return isManager || isRealmAdmin
    if (k.startsWith('admin-')) return isRouteVisible(k, isManager, isRealmAdmin)
    return true
  })
}

export function HelpProvider({ children }: React.PropsWithChildren) {
  const account = useAccount()
  const router = useRouter()
  const voice = useOptionalPageToolsContext()
  const voiceRef = useRef(voice)
  voiceRef.current = voice

  const signedIn = !!account.user
  const customer = account.customer?.name ?? ''
  const job = (router.params?.job as string) || account.job?.name || ''
  const reader = helpReaderFor(account.user?.role_permissions)
  const available = signedIn && !router.meta?.hideAssistant

  const [open, setOpen] = useState(false)

  const permsRef = useRef(account.user?.role_permissions)
  permsRef.current = account.user?.role_permissions
  const bridgeRef = useRef<ReturnType<typeof createHelpBridge> | null>(null)
  if (!bridgeRef.current) {
    bridgeRef.current = createHelpBridge(
      () => voiceRef.current?.livePageTools() ?? [],
      undefined,
      (page) => voiceRef.current?.navigateToPage(page) ?? { ok: false, error: 'going to another page is not available here' },
      () => navPagesFor(permsRef.current),
    )
  }

  const ai = useAiAccess(signedIn ? customer : undefined)
  const { tours } = useHelpTours({ customer, enabled: signedIn && open })
  const starters = useHelpStarters({ customer, enabled: signedIn && open })
  const chat = useHelpChat({
    customer,
    job,
    route: router.name,
    bridge: bridgeRef.current,
    pageContext: () => voiceRef.current?.pageContext() ?? '',
    tour: () => tourContext(runner.current()),
  })

  // The sections and features this person may be told about.
  const readable = sectionsFor(reader)
  const routeSection = sectionForRoute(router.name)
  const section = routeSection ? readable.find((s) => s.key === routeSection.key) : undefined
  const featureOf = (key?: string): Feature | undefined => {
    const [sectionKey, featureKey] = (key ?? '').split('/')
    return readable.find((s) => s.key === sectionKey)?.features.find((f) => f.key === featureKey)
  }

  const runner = useTourRunner({
    tools: () => voiceRef.current?.livePageTools() ?? [],
    navigateToPage: (page) => voiceRef.current?.navigateToPage(page) ?? null,
    routeName: router.name,
    pageKey: voice?.pageKey,
    explain: chat.explainStep,
    note: chat.addNote,
    describe: (step) => featureOf(step.feature)?.name || step.control || step.say || 'this page',
  })
  const tour = runner.state

  // A new section with a conversation open: one line says where the person
  // is now. The conversation is kept; a tour in flight is not ended (tours
  // move between pages on purpose). Not mid-turn: the model says it then.
  const noteRef = useRef({ open, count: chat.messages.length, loading: chat.isLoading, addNote: chat.addNote, phase: runner.state.phase })
  noteRef.current = { open, count: chat.messages.length, loading: chat.isLoading, addNote: chat.addNote, phase: runner.state.phase }
  const lastSectionRef = useRef<string | undefined>(section?.key)
  useEffect(() => {
    if (!section) return
    const previous = lastSectionRef.current
    lastSectionRef.current = section.key
    const now = noteRef.current
    if (previous && previous !== section.key && now.open && now.count > 0 && !now.loading && now.phase !== 'navigating') now.addNote(`Now on ${section.title}.`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section?.key])

  const handleStartTour = (run: TourRun) => runner.start(run)
  const handleNext = () => runner.next()
  const handleBack = () => runner.back()
  const handleEndTour = () => runner.end()

  // A question mid-tour keeps the step.
  const handleSend = async (text: string) => {
    const asking = runner.current().phase === 'explaining'
    if (asking) runner.ask()
    await chat.send(text)
    if (asking) runner.answered()
  }

  // "Send to the agent": the person's click puts a TYPE step's text in the
  // page's agent composer and sends it. Never sent without the click.
  const handleSendToAgent = async (text: string) => {
    const tools = voiceRef.current?.livePageTools() ?? []
    const setter = tools.find((t) => t.name === 'set_message' || t.name === 'set_question')
    const sender = tools.find((t) => t.name === 'send_message' || t.name === 'send_question')
    if (!setter || !sender) {
      chat.addNote(`Open the Agent to send this: “${text}”`)
      return
    }
    await setter.execute({ value: text })
    await new Promise((r) => setTimeout(r, COMPOSER_SETTLE_MS))
    let sent = false
    try {
      sent = !JSON.parse(String(await sender.execute({}))).error
    } catch {
      sent = false
    }
    chat.addNote(sent ? `Sent to the agent: “${text}”` : 'The message is in the agent’s composer. Press Send to send it.')
  }

  const handleOpen = () => setOpen(true)
  const handleClose = () => {
    handleEndTour()
    setOpen(false)
  }

  const step = currentStep(tour)
  const stepFeature = featureOf(step?.feature)
  // Show me around: the customer's tours, this area, then every other area
  // the person may read that has a page of its own to go to. A section with
  // a built-in tour (help/tours) gets it; the rest a step per feature.
  const aroundTour = (s: (typeof readable)[number]) => BUILT_IN_TOURS[s.key]?.(reader) ?? tourFromSection(s, reader)
  const tourChoices: TourChoiceGroup[] = [
    { heading: 'Tours', tours: tours.map(tourFromHelpTour).filter((t) => t.steps.length > 0) },
    { heading: 'This area', tours: section ? [aroundTour(section)].filter((t) => t.steps.length > 0) : [] },
    {
      heading: 'Other areas',
      tours: readable
        .filter((s) => s.key !== section?.key && s.navPage && NAV_PAGES[s.navPage])
        .map(aroundTour)
        .filter((t) => t.steps.length > 0),
    },
  ]
  const allowed = ai.can('help')
  // Pills until the person's first message; Reset brings them back. None during a tour.
  const asked = chat.messages.some((m) => m.role === 'user')
  const prompts = section && !asked && !tour.tour ? starterQuestionsFor(section.key, starters.records).questions : undefined
  // The greeting goes with the pills: the time of day, the first name, the section's opening line.
  const firstName = useFirstName()
  const greeting = section && !asked && !tour.tour
    ? paneOpening({ greeting: timeOfDayGreeting(), firstName, intro: sectionIntroFor(section.key, starters.records).intro, afterIntroRows: false }).top
    : undefined

  const startTour = (run: TourRun) => {
    setOpen(true)
    runner.start(run)
  }

  const value: HelpContextValue = { available, open, handleOpen, handleClose, startTour, tour, endTour: handleEndTour }

  return (
    <HelpContext.Provider value={value}>
      {children}
      {available && open && (
        <HelpPane
          sectionTitle={section?.title}
          starters={prompts}
          greeting={greeting}
          messages={chat.messages}
          isLoading={chat.isLoading}
          error={chat.error}
          disabledReason={!allowed ? ai.refusalFor('help') : !section ? 'There is no help for this page yet.' : undefined}
          onSend={handleSend}
          onReset={chat.reset}
          onClose={handleClose}
          renderMessage={(message, index) =>
            renderChatMessage(message, index, {
              effectivePlatinumData: undefined,
              graphScreenshots: new Map(),
              setGraphScreenshots: () => undefined,
              onChatToggle: handleClose,
              uiCalls: chat.uiCalls,
              onConfirmUiCall: (id) => void chat.confirmUiCall(id),
              onCancelUiCall: chat.cancelUiCall,
              router: { name: router.name, params: router.params, navigate: router.navigate },
            })
          }
          tourChoices={tourChoices}
          onStartTour={handleStartTour}
          tour={
            tour.tour && step
              ? {
                  name: tour.tour.name,
                  number: tour.index + 1,
                  total: tour.tour.steps.length,
                  featureName: stepFeature?.name,
                  say: step.say,
                  type: step.type,
                  did: tour.did,
                  nextLabel: tour.tour.steps[tour.index + 1]?.action?.label,
                  navigating: tour.phase === 'navigating',
                }
              : undefined
          }
          onNext={handleNext}
          onBack={handleBack}
          onEndTour={handleEndTour}
          onSendToAgent={(text) => void handleSendToAgent(text)}
        />
      )}
    </HelpContext.Provider>
  )
}

export default HelpProvider
